import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { AxiosError } from 'axios'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button, CloseIcon, CustomTooltip } from 'components/facets'
import { AnimatedLockIcon } from 'components/facets/AnimatedLockIcon'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { TableSearchNavigator } from 'components/facets/TableSearchNavigator'
import {
  deletePurchase,
  deleteReceipt,
  downloadReceipt,
  getJobData,
  getUploadedReceipt,
  runSplittingStrategy,
  updatePurchaseEvent,
  updatePurchases,
  updateSeller,
  uploadReceipt,
} from 'lib/api'
import { useAppContext, useWindowDimensions } from 'lib/hooks'
import { useFetchPurchaseArticles } from 'lib/hooks/useFetchPurchaseArticles'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { getCurrencySymbol } from 'lib/utils'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import Dropzone from 'react-dropzone'
import { Trans, useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table, TableRowRenderer } from 'react-virtualized'
import {
  CardLanguage,
  DraftPurchaseEvent,
  formatDateToYYYYMMDD,
  PurchaseArticle,
  PurchaseEvent,
  Seller,
  SUPPORTED_CURRENCIES,
} from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'

export type SplittingStrategyUiValue = number | string
export const PurchaseEventDetails = ({
  lot,
  handleClose,
  purchaseEvents,
  setPurchaseEvents,
  setHasPurchases,
  viewOnly = false,
}: {
  lot: PurchaseEvent
  handleClose: () => void
  purchaseEvents: PurchaseEvent[]
  setPurchaseEvents: (events: PurchaseEvent[]) => void
  setHasPurchases: React.Dispatch<React.SetStateAction<boolean>>
  viewOnly?: boolean
}): JSX.Element => {
  // const { user } = useUser()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { width } = useWindowDimensions()
  const { isMobile } = useAppContext()
  const { fetchArticles } = useFetchPurchaseArticles()
  const { updateStoresPurchaseEvent } = useFetchPurchaseEvents()
  const [loading, setLoading] = useState(true)
  const [activeEditingMode, setActiveEditingMode] = useState(false)

  const [articles, setArticles] = useState<PurchaseArticle[]>([])
  const [totalQuantity, setTotalQuantity] = useState<number | undefined>(0)
  const [totalPrice, setTotalPrice] = useState<number | undefined>(undefined)
  const [newTotalPrice, setNewTotalPrice] = useState<number | undefined>()
  const [newTotalQuantity, setNewTotalQuantity] = useState<number | undefined>()

  const [modifiedArticles, setModifiedArticles] = useState<Set<number>>(new Set())
  const [modifiedSeller, setModifiedSeller] = useState<Seller>(lot?.seller ?? ({} as Seller))
  const [workingPurchaseEvent, setWorkingPurchaseEvent] = useState<DraftPurchaseEvent>({
    purchaseEvent: _.cloneDeep(lot),
    purchases: [],
  })

  const [searchText, setSearchText] = useState('')
  const [matchIndices, setMatchIndices] = useState<number[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)

  // Receipt state
  const [uploadedReceiptName, setUploadedReceiptName] = useState<string | null>(null)
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [receiptFileContent, setReceiptFileContent] = useState<string | null>(null)
  const [receiptContentType, setReceiptContentType] = useState<string | null>(null)
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false)

  const tableRef = useRef<any>(null)
  const [articleToDelete, setArticleToDelete] = useState<PurchaseArticle | null>(null)
  const [confirmDeleteReceipt, setConfirmDeleteReceipt] = useState(false)

  const splittingPollRef = useRef<number | null>(null)

  const clearPolling = useCallback(() => {
    if (splittingPollRef.current) {
      clearInterval(splittingPollRef.current)
      splittingPollRef.current = null
    }
  }, [])

  useEffect(() => {
    return clearPolling
  }, [clearPolling])

  // Fetch existing receipt
  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const { hasReceipt, filename } = await getUploadedReceipt(lot.id)
        setUploadedReceiptName(hasReceipt ? filename ?? null : null)
      } catch {
        setUploadedReceiptName(null)
      }
    }
    fetchReceipt()
  }, [lot.id])

  const handleReceiptDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return
    const file = acceptedFiles[0]
    setReceiptFile(file)
    setReceiptContentType(file.type)

    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result
      if (typeof result === 'string') setReceiptFileContent(result)
    }
    reader.readAsDataURL(file)
  }

  const handleReceiptUpload = async () => {
    if (!receiptFile || !receiptFileContent || !receiptContentType) return
    try {
      setIsUploadingReceipt(true)
      await uploadReceipt(lot.id, receiptFile.name, receiptContentType, receiptFileContent)
      enqueueSnackbar(`${t('taxation.success.fileUploaded')}`, { variant: 'success' })
      setUploadedReceiptName(receiptFile.name)
      setReceiptFile(null)
      setReceiptFileContent(null)
      setReceiptContentType(null)
      setPurchaseEvents(
        purchaseEvents.map((pe) => (pe.id === lot.id ? { ...pe, hasReceipt: true } : pe))
      )
    } catch {
      enqueueSnackbar(`${t('taxation.errors.errorDuringFileUpload')}`, { variant: 'error' })
    } finally {
      setIsUploadingReceipt(false)
    }
  }

  const handleReceiptDownload = async () => {
    try {
      await downloadReceipt(lot.id)
    } catch {
      enqueueSnackbar(`${t('taxation.errors.errorDuringFileDownload')}`, { variant: 'error' })
    }
  }

  const handleReceiptDelete = async () => {
    try {
      await deleteReceipt(lot.id)
      setUploadedReceiptName(null)
      setPurchaseEvents(
        purchaseEvents.map((pe) => (pe.id === lot.id ? { ...pe, hasReceipt: false } : pe))
      )
      enqueueSnackbar(`${t('taxation.success.receiptDeleted')}`, { variant: 'success' })
    } catch {
      enqueueSnackbar(`${t('taxation.errors.errorDeletingReceipt')}`, { variant: 'error' })
    }
  }

  // Search effect
  useEffect(() => {
    if (!searchText.trim()) {
      setMatchIndices([])
      setCurrentMatchIndex(0)
      return
    }

    const lowerSearch = searchText.toLowerCase()
    const searchTarget = articles
    const matches = searchTarget.reduce<number[]>((acc, article, index) => {
      const nameMatch =
        article.card.name.toLowerCase().includes(lowerSearch) ||
        article.card.expansion.toLowerCase().includes(lowerSearch) ||
        article.card.set.toLowerCase().includes(lowerSearch) ||
        article.card.cn?.toLowerCase().includes(lowerSearch)

      if (nameMatch) acc.push(index)
      return acc
    }, [])

    setMatchIndices(matches)
    setCurrentMatchIndex(0)
  }, [searchText, articles])

  // Scroll to current match
  useEffect(() => {
    if (matchIndices.length > 0 && tableRef.current) {
      tableRef.current.scrollToRow(matchIndices[currentMatchIndex])
    }
  }, [matchIndices, currentMatchIndex])

  useEffect(() => {
    if (articles.length === 0) {
      setNewTotalPrice(totalPrice)
      setNewTotalQuantity(totalQuantity)
      return
    }

    const totals = articles.reduce(
      (acc, a) => {
        const effectiveQty = a.newQuantity ?? a.quantity
        const effectivePrice = a.newPrice ?? a.price

        acc.totalQuantity += effectiveQty
        acc.totalPriceInCents += Math.round(effectivePrice * 100)
        return acc
      },
      { totalQuantity: 0, totalPriceInCents: 0 }
    )

    setNewTotalPrice(totals.totalPriceInCents / 100)
    setNewTotalQuantity(totals.totalQuantity)
    setWorkingPurchaseEvent((prev) => {
      if (!prev) return prev

      return {
        ...prev,
      }
    })
  }, [articles])

  const fetchPuchases = useCallback(async () => {
    if (!lot) return
    setLoading(true)
    try {
      const stock = await fetchArticles(lot?.id)
      const quantity = stock.reduce((acc, article) => acc + article.quantity, 0)
      const price = stock.reduce((acc, a) => acc + Math.round(a.price * 100), 0) / 100

      setTotalPrice(price)
      setTotalQuantity(quantity)
      setArticles(stock)

      setWorkingPurchaseEvent((prev) => ({
        ...prev,
        purchases: stock,
      }))

      if (stock.length > 0) setHasPurchases(true)
      else setHasPurchases(false)
    } catch (e) {
      console.error(e)
      enqueueSnackbar(`${t('taxation.errors.errorLoadingArticles')}`, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [fetchArticles, lot])

  useEffect(() => {
    fetchPuchases()
  }, [lot.id])

  useEffect(() => {
    if (loading) return
    const pe = workingPurchaseEvent.purchaseEvent
    const qty = newTotalQuantity ?? totalQuantity
    const price = newTotalPrice ?? totalPrice
    const hasQtyMismatch = pe.quantity != null && qty !== pe.quantity
    const hasPriceMismatch = pe.price != null && price != null && Math.abs(price - pe.price) > 0.01
    if (hasQtyMismatch || hasPriceMismatch) {
      setActiveEditingMode(true)
    }
  }, [loading])

  const rowRenderer: TableRowRenderer = ({ index, key, style, columns, className, rowData }) => {
    rowData
    const isHighlighted = matchIndices.includes(index)
    return (
      <div
        key={key}
        style={style}
        className={`${className} ${isHighlighted ? 'highlight-row' : ''}`}
      >
        {columns}
      </div>
    )
  }

  const handleChange = (
    value: number | boolean,
    index: number,
    attribute: keyof PurchaseArticle
  ) => {
    const copyOfDraft: DraftPurchaseEvent = _.cloneDeep(workingPurchaseEvent)

    setArticles((prev) => {
      const updated = [...prev]
      const newArticle = prev[index].copy()

      const draftIdx = copyOfDraft.purchases.findIndex(
        (item) => item.purchaseId === newArticle.purchaseId
      )

      if (attribute === 'newQuantity') {
        const newQty = value as number
        const currentQty = newArticle.newQuantity ?? newArticle.quantity
        const currentTotal = newArticle.newPrice ?? newArticle.price
        const unitPrice = currentQty > 0 ? currentTotal / currentQty : 0
        const newTotal = unitPrice * newQty

        newArticle.newQuantity = newQty
        newArticle.newPrice = newTotal
        copyOfDraft.purchases![draftIdx].newQuantity = newQty
        copyOfDraft.purchases![draftIdx].newPrice = newTotal
      } else if (attribute === 'newPrice') {
        newArticle.newPrice = value as number
        newArticle.priceLock = true

        copyOfDraft.purchases![draftIdx].newPrice = value as number
        copyOfDraft.purchases![draftIdx].priceLock = true
      } else if (attribute === 'isVat') {
        newArticle.isVat = value as boolean
      } else if (attribute === 'priceLock') {
        newArticle.priceLock = value as boolean
        copyOfDraft.purchases![draftIdx].priceLock = value as boolean
      }

      updated[index] = newArticle

      return updated
    })

    setWorkingPurchaseEvent(copyOfDraft)
    setModifiedArticles((prev) => new Set(prev).add(Number(articles[index].purchaseId)))
  }

  const handleConvertAllItems = (lock: boolean) => {
    setArticles((prevArticles) => {
      const nextArticles = prevArticles.map((a) => {
        const copy = a.copy()
        copy.priceLock = lock
        return copy
      })

      setWorkingPurchaseEvent((prevDraft) => {
        const nextDraftPurchases = prevDraft.purchases.map((p) => {
          const copy = p.copy()
          copy.priceLock = lock
          return copy
        })

        return {
          ...prevDraft,
          purchases: nextDraftPurchases,
        }
      })

      setModifiedArticles(() => new Set(nextArticles.map((a) => Number(a.purchaseId))))
      return nextArticles
    })
  }

  const handleSaveChanges = async () => {
    const effectiveLot = workingPurchaseEvent.purchaseEvent
    const lotChanged =
      JSON.stringify({ ...effectiveLot, seller: undefined }) !==
      JSON.stringify({ ...lot, seller: undefined })

    const sellerChanged = JSON.stringify(modifiedSeller) !== JSON.stringify(lot?.seller ?? {})

    const hasArticleChanges = modifiedArticles.size > 0

    if (!hasArticleChanges && !lotChanged && !sellerChanged) return

    // setLoading(true)

    // We maintain an up-to-date local copy of PurchaseEvents to use for final upgrade
    let updatedEvents = [...purchaseEvents]

    const articlesToSave = hasArticleChanges
      ? articles.filter((a) => modifiedArticles.has(Number(a.purchaseId)))
      : []
    setModifiedArticles(new Set())

    // update purchase event
    if (lotChanged) {
      try {
        await updatePurchaseEvent(effectiveLot)

        updatedEvents = updatedEvents.map((ev) =>
          ev.id === effectiveLot.id
            ? {
                ...ev,
                ...effectiveLot,
                // totalRegisteredPrice: totalPrice,
                // totalRegisteredQuantity: totalQty,
              }
            : ev
        )

        updateStoresPurchaseEvent(effectiveLot)

        enqueueSnackbar(`${t('taxation.success.lotUpdated')}`, { variant: 'success' })
      } catch (err) {
        console.error(err)
        enqueueSnackbar(`${t('taxation.errors.lotNotUpdated')}`, { variant: 'error' })
      }
    }

    // update purchases
    if (hasArticleChanges) {
      try {
        await updatePurchases(articlesToSave)

        // Apply saved values to local articles (commit newPrice/newQuantity as current values)
        setArticles((prev) =>
          prev.map((a) => {
            const saved = articlesToSave.find((s) => s.purchaseId === a.purchaseId)
            if (!saved) return a
            const copy = a.copy()
            if (copy.newPrice !== undefined) {
              copy.price = copy.newPrice
              copy.newPrice = undefined
            }
            if (copy.newQuantity !== undefined) {
              copy.quantity = copy.newQuantity
              copy.newQuantity = undefined
            }
            return copy
          })
        )

        enqueueSnackbar(`${t('taxation.success.articlesUpdated')}`, { variant: 'success' })
      } catch (err) {
        console.error(err)
        enqueueSnackbar(`${t('taxation.errors.articlesNotUpdated')}`, { variant: 'error' })
      }
    }

    // update seller
    if (sellerChanged) {
      try {
        await updateSeller(lot.id, modifiedSeller)

        updatedEvents = updatedEvents.map((ev) =>
          ev.id === lot?.id ? { ...ev, seller: modifiedSeller } : ev
        )

        updateStoresPurchaseEvent({
          ...workingPurchaseEvent.purchaseEvent!,
          seller: modifiedSeller,
        })

        enqueueSnackbar(`${t('taxation.success.sellerUpdated')}`, { variant: 'success' })
      } catch (err) {
        console.error(err)
        enqueueSnackbar(`${t('taxation.errors.sellerNotUpdate')}`, { variant: 'error' })
      }
    }

    // Update the store only once at the end
    setPurchaseEvents(updatedEvents)
  }

  const handleDelete = async (article: PurchaseArticle) => {
    try {
      const response = await deletePurchase(Number(lot!.id), Number(article.purchaseId))
      if (response) {
        const newArticles = articles.filter((items) => items.purchaseId !== article.purchaseId)
        setArticles(newArticles)
        enqueueSnackbar(`${t('taxation.success.articleDeleted')}`, { variant: 'success' })
      }
    } catch (e) {
      console.log(e)
      enqueueSnackbar(`${t('taxation.errors.articleNotDeleted')}`, { variant: 'error' })
    }
  }

  const handleReset = (article: PurchaseArticle) => {
    setArticles((prev) => {
      const updated = [...prev]
      const index = prev.findIndex((a) => a.purchaseId === article.purchaseId)

      if (index !== -1) {
        const resetArticle = new PurchaseArticle({
          ...prev[index].toPlainObject(),
          newPrice: undefined,
          newQuantity: undefined,
          priceLock: false,
        })

        updated[index] = resetArticle
      }

      return updated
    })

    setWorkingPurchaseEvent({
      ...workingPurchaseEvent,
      purchases: workingPurchaseEvent.purchases.map((d) => {
        return d.purchaseId === article.purchaseId
          ? new PurchaseArticle({
              ...d,
              newPrice: undefined,
              newQuantity: undefined,
              priceLock: false,
            })
          : d
      }),
    })

    // Remove the item from the edited set
    setModifiedArticles((prev) => {
      const updated = new Set(prev)
      updated.delete(Number(article.purchaseId))
      return updated
    })
  }

  const handleRunSplittingStrategy = async (workingPe: DraftPurchaseEvent) => {
    if (!lot) return
    setLoading(true)

    // if there is already an active poll, I close it
    if (splittingPollRef.current) {
      clearInterval(splittingPollRef.current)
      splittingPollRef.current = null
    }

    try {
      // 1) load job
      const { jobId } = await runSplittingStrategy(lot.id, workingPe)

      enqueueSnackbar(`${t('taxation.rescaleStarted')}`, { variant: 'info' })

      // 2) polling
      splittingPollRef.current = window.setInterval(async () => {
        try {
          const statusRes = await getJobData(jobId)
          const status = (statusRes as any)?.status ?? statusRes

          if (status === 'completed') {
            if (splittingPollRef.current) clearInterval(splittingPollRef.current)
            splittingPollRef.current = null

            // 3) completed -> refetch DB state
            const stock = await fetchArticles(lot.id)

            const quantity = stock.reduce((acc, article) => acc + article.quantity, 0)
            const price = stock.reduce((acc, a) => acc + Math.round(a.price * 100), 0) / 100

            setArticles(stock)
            setTotalQuantity(quantity)
            setTotalPrice(price)

            setPurchaseEvents(
              purchaseEvents.map((ev) =>
                ev.id === lot.id
                  ? { ...ev, totalRegisteredPrice: price, totalRegisteredQuantity: quantity }
                  : ev
              )
            )

            // reset draft + modified
            setWorkingPurchaseEvent((prev) => ({
              ...prev,
              purchases: stock,
            }))
            setModifiedArticles(new Set())

            setLoading(false)
            enqueueSnackbar(`${t('taxation.rescaleApplied')}`, { variant: 'success' })
          }

          if (status === 'failed') {
            if (splittingPollRef.current) clearInterval(splittingPollRef.current)
            splittingPollRef.current = null

            setLoading(false)
            const failedKey = `taxation.errors.${statusRes.failedReason}`
            enqueueSnackbar(t(failedKey, t('taxation.errors.errorStartingSplittingStrategy')), {
              variant: 'error',
            })
          }
        } catch (err) {
          console.error('Polling error (splitting strategy):', err)
        }
      }, 3000)
    } catch (e) {
      console.error(e)
      setLoading(false)

      const err = e as AxiosError<{ status: number; message: string }>
      const message = err.response?.data?.message
      const fallback = t('taxation.errors.errorStartingSplittingStrategy')
      if (message) {
        const key = `taxation.${message}`
        enqueueSnackbar(t(key, fallback), { variant: 'error' })
      } else {
        enqueueSnackbar(fallback, { variant: 'error' })
      }
    }
  }

  const getArticleLocalizedName = (article: PurchaseArticle) => {
    const idLanguage = article.idLanguage
    if (idLanguage === CardLanguage.English) {
      return article.card.name
    } else if (idLanguage === CardLanguage.German) {
      return article.card.locNames.DE
    } else if (idLanguage === CardLanguage.French) {
      return article.card.locNames.FR
    } else if (idLanguage === CardLanguage.Spanish) {
      return article.card.locNames.ES
    } else if (idLanguage === CardLanguage.Italian) {
      return article.card?.locNames.IT
    }
  }

  // TODO: fix width after merge mobile PR
  return (
    <>
      <Box width={isMobile ? '100%' : width - 80 - 260} padding={isMobile ? 2 : 4}>
        <div className="d-flex align-items-center justify-content-start mb-2">
          <IconButton
            onClick={() => {
              clearPolling()
              handleClose()
            }}
            data-testid="close-btn"
          >
            <CloseIcon />
          </IconButton>
          {!viewOnly && !activeEditingMode && (
            <Button
              variant="contained"
              className="ms-3"
              color="primary"
              onClick={() => setActiveEditingMode(true)}
              data-testid="edit-pe"
            >
              {t('taxation.editLot')}
            </Button>
          )}
        </div>
        <div className="w-100 h-100">
          {/* Receipt */}
          <div
            className="d-flex align-items-center mb-3"
            style={{ gap: 16 }}
            data-testid="drawer-receipt-section"
          >
            <h4 className="m-0">{t('taxation.lotInformation.receipt')}:</h4>
            {uploadedReceiptName ? (
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <div onClick={handleReceiptDownload} style={{ cursor: 'pointer' }}>
                  <i className="fa-light fa-arrow-down-to-bracket table-action-icon" />
                </div>
                <span>{uploadedReceiptName}</span>
                <div onClick={() => setConfirmDeleteReceipt(true)} style={{ cursor: 'pointer' }}>
                  <i className="fa-light fa-trash table-action-icon" />
                </div>
              </div>
            ) : (
              <span style={{ color: '#888' }}>{t('taxation.noFilesUploaded')}</span>
            )}
            <Dropzone
              onDrop={handleReceiptDrop}
              multiple={false}
              accept={['.pdf', '.png', '.jpg', '.jpeg']}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                // @ts-ignore
                <div
                  {...getRootProps()}
                  style={{
                    border: '1px dashed #aaa',
                    borderRadius: 6,
                    padding: '6px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: isDragActive ? '#f0f0f0' : 'transparent',
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <input {...getInputProps()} />
                  {receiptFile ? (
                    <span>
                      <strong>{t('taxation.selectedFile')}</strong> {receiptFile.name}
                    </span>
                  ) : isDragActive ? (
                    <span>{t('taxation.dropFileHere')}</span>
                  ) : (
                    <span>{t('taxation.dragHere')}</span>
                  )}
                </div>
              )}
            </Dropzone>
            {receiptFile && (
              <>
                {uploadedReceiptName && (
                  <span style={{ color: '#c57600', fontSize: 13 }}>
                    <i className="fa-light fa-triangle-exclamation me-1" />
                    {t('taxation.receiptWillBeOverwritten')}
                  </span>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setReceiptFile(null)
                    setReceiptFileContent(null)
                    setReceiptContentType(null)
                  }}
                >
                  {t('common.remove')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleReceiptUpload}
                  disabled={isUploadingReceipt}
                >
                  {isUploadingReceipt ? t('common.loading') : t('taxation.loadReceipt')}
                </Button>
              </>
            )}
          </div>

          <div className="mb-3">
            <h4>{t('taxation.lotInformation.lotInformation')}</h4>
            {/* Purchase Date | Lot Number | Quantity | Price | Currency | VAT toggle */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={1}>
                <TextField
                  label={t('taxation.lotInformation.purchaseDate')}
                  value={formatDateToYYYYMMDD(workingPurchaseEvent.purchaseEvent.purchaseDate)}
                  fullWidth
                  type="date"
                  disabled={!activeEditingMode}
                  onChange={(e) => {
                    const purchaseDate = new Date(e.target.value)
                    if (isNaN(purchaseDate.getTime())) return
                    setWorkingPurchaseEvent((prev) => ({
                      ...prev,
                      purchaseEvent: {
                        ...prev.purchaseEvent,
                        purchaseDate,
                      },
                    }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  fullWidth
                  disabled={!activeEditingMode}
                  label={t('taxation.lotInformation.lotNumber')}
                  value={workingPurchaseEvent.purchaseEvent.lotNumber}
                  onChange={(e) => {
                    const lotNumber = String(e.target.value)
                    setWorkingPurchaseEvent((prev) => ({
                      ...prev,
                      purchaseEvent: {
                        ...prev.purchaseEvent,
                        lotNumber,
                      },
                    }))
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  fullWidth
                  disabled={!activeEditingMode}
                  onChange={(e) => {
                    const quantity = e.target.value === '' ? undefined : Number(e.target.value)
                    setWorkingPurchaseEvent((prev) => ({
                      ...prev,
                      purchaseEvent: {
                        ...prev.purchaseEvent,
                        quantity,
                      },
                    }))
                  }}
                  type="number"
                  label={t('taxation.lotInformation.totalQuantity')}
                  value={workingPurchaseEvent.purchaseEvent.quantity ?? ''}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  fullWidth
                  disabled={!activeEditingMode}
                  type="number"
                  label={t('taxation.lotInformation.totalPrice')}
                  onChange={(e) => {
                    const price = e.target.value === '' ? undefined : Number(e.target.value)
                    setWorkingPurchaseEvent((prev) => ({
                      ...prev,
                      purchaseEvent: {
                        ...prev.purchaseEvent,
                        price,
                      },
                    }))
                  }}
                  value={workingPurchaseEvent.purchaseEvent.price ?? ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {getCurrencySymbol(workingPurchaseEvent.purchaseEvent.currency)}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={1}>
                <FormControl fullWidth>
                  <InputLabel>{t('taxation.lotInformation.currency')}</InputLabel>
                  <Select
                    value={workingPurchaseEvent.purchaseEvent.currency}
                    onChange={(e) => {
                      const currency = e.target.value as string
                      setWorkingPurchaseEvent((prev) => ({
                        ...prev,
                        purchaseEvent: {
                          ...prev.purchaseEvent,
                          currency,
                        },
                      }))
                    }}
                    disabled={!activeEditingMode}
                  >
                    {SUPPORTED_CURRENCIES.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} lg={3} className="d-flex align-items-center">
                <ToggleButtonGroup
                  value={workingPurchaseEvent.purchaseEvent.withVat ? 'commercial' : 'private'}
                  exclusive
                  onChange={(_e, value) => {
                    if (value !== null && activeEditingMode) {
                      const withVat = value === 'commercial'
                      setWorkingPurchaseEvent((prev) => ({
                        ...prev,
                        purchaseEvent: {
                          ...prev.purchaseEvent,
                          withVat,
                        },
                      }))
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="private" disabled={!activeEditingMode}>
                    {t('taxation.lotInformation.privateSale')}
                  </ToggleButton>
                  <ToggleButton value="commercial" disabled={!activeEditingMode}>
                    {t('taxation.lotInformation.commercialSale')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </div>

          {/* Seller info */}
          <div className="mb-3 mt-4">
            <h4>{t('taxation.seller.sellerInfo')}</h4>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={6}>
                <TextField
                  label={t('taxation.seller.sellerName')}
                  value={modifiedSeller?.name ?? ''}
                  fullWidth
                  disabled={!activeEditingMode}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      name: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={6}>
                <TextField
                  fullWidth
                  disabled={!activeEditingMode}
                  label={t('taxation.seller.vatNumber')}
                  value={modifiedSeller?.vatNumber ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      vatNumber: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={6}>
                <TextField
                  label={t('taxation.seller.addressLine1')}
                  value={modifiedSeller?.addressLine1 ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      addressLine1: e.target.value,
                    })
                  }
                  fullWidth
                  disabled={!activeEditingMode}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={6}>
                <TextField
                  label={t('taxation.seller.addressLine2')}
                  fullWidth
                  disabled={!activeEditingMode}
                  value={modifiedSeller?.addressLine2 ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      addressLine2: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  label={t('taxation.seller.zipCode')}
                  fullWidth
                  disabled={!activeEditingMode}
                  value={modifiedSeller?.zipCode ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      zipCode: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  label={t('taxation.seller.country')}
                  fullWidth
                  disabled={!activeEditingMode}
                  value={modifiedSeller?.country ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      country: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={4}>
                <TextField
                  label={t('taxation.seller.city')}
                  fullWidth
                  disabled={!activeEditingMode}
                  value={modifiedSeller?.city ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      city: e.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={4}>
                <TextField
                  label={t('taxation.seller.province')}
                  fullWidth
                  disabled={!activeEditingMode}
                  value={modifiedSeller?.province ?? ''}
                  onChange={(e) =>
                    setModifiedSeller({
                      ...modifiedSeller,
                      province: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          </div>

          {loading ? (
            <div className="w-100 d-flex flex-column align-items-center loading-progress">
              <h6 className="pb-5">{t('common.loading')}</h6>
              <CircularProgress size={100} />
            </div>
          ) : (
            <div className="mt-5">
              <h4 className="text-capitalize">{t('taxation.items')}</h4>

              <Grid container spacing={2}>
                {workingPurchaseEvent.purchaseEvent.quantity != null &&
                  (newTotalQuantity ?? totalQuantity) !==
                    workingPurchaseEvent.purchaseEvent.quantity && (
                    <Grid item xs={12} md={6}>
                      <div
                        className="warning-box mb-0 d-flex flex-column justify-content-between"
                        style={{ fontSize: '0.8rem', height: '100%' }}
                        data-testid="quantity-mismatch"
                      >
                        <div className="d-flex flex-row align-items-center mb-1">
                          <i className="fa-light fa-triangle-exclamation me-2 fs-6" />
                          <strong>{t('taxation.quantityMismatch')}</strong>
                        </div>
                        <p className="p-0 m-0">
                          <Trans
                            i18nKey="taxation.quantityMismatchDesc"
                            values={{
                              expected: workingPurchaseEvent.purchaseEvent.quantity,
                              actual: newTotalQuantity ?? totalQuantity,
                            }}
                            components={{ bold: <strong /> }}
                          />
                        </p>
                        <div className="d-flex flex-row mt-2">
                          <Button
                            variant="outlined"
                            size="small"
                            style={{ fontSize: '0.75rem' }}
                            data-testid="set-pe-quantity-btn"
                            disabled={!activeEditingMode}
                            onClick={() => {
                              const qty = newTotalQuantity ?? totalQuantity
                              setWorkingPurchaseEvent((prev) => ({
                                ...prev,
                                purchaseEvent: { ...prev.purchaseEvent, quantity: qty },
                              }))
                            }}
                          >
                            {t('taxation.updateLotQuantity', {
                              value: newTotalQuantity ?? totalQuantity,
                            })}
                          </Button>
                        </div>
                      </div>
                    </Grid>
                  )}

                {workingPurchaseEvent.purchaseEvent.price != null &&
                  (newTotalPrice ?? totalPrice) != null &&
                  Math.abs(
                    (newTotalPrice ?? totalPrice)! - workingPurchaseEvent.purchaseEvent.price
                  ) > 0.01 && (
                    <Grid item xs={12} md={6}>
                      <div
                        className="warning-box mb-0 d-flex flex-column justify-content-between"
                        style={{ fontSize: '0.8rem', height: '100%' }}
                        data-testid="price-mismatch"
                      >
                        <div className="d-flex flex-row align-items-center mb-1">
                          <i className="fa-light fa-triangle-exclamation me-2 fs-6" />
                          <strong>{t('taxation.priceMismatch')}</strong>
                        </div>
                        <p className="p-0 m-0">
                          <Trans
                            i18nKey="taxation.priceMismatchDesc"
                            values={{
                              expected: `${
                                workingPurchaseEvent.purchaseEvent.price
                              } ${getCurrencySymbol(workingPurchaseEvent.purchaseEvent.currency)}`,
                              actual: `${newTotalPrice ?? totalPrice} ${getCurrencySymbol(
                                workingPurchaseEvent.purchaseEvent.currency
                              )}`,
                            }}
                            components={{ bold: <strong /> }}
                          />
                        </p>
                        {(() => {
                          const lockedCount = articles.filter((a) => a.priceLock).length
                          if (lockedCount === 0) return null
                          return (
                            <p className="p-0 m-0 mt-1">
                              {t('taxation.priceMismatchLockedNote', { lockedCount })}
                            </p>
                          )
                        })()}
                        <div className="d-flex flex-row mt-2" style={{ gap: 8 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            style={{ fontSize: '0.75rem' }}
                            disabled={!activeEditingMode}
                            onClick={() => {
                              const price = newTotalPrice ?? totalPrice
                              setWorkingPurchaseEvent((prev) => ({
                                ...prev,
                                purchaseEvent: { ...prev.purchaseEvent, price },
                              }))
                            }}
                          >
                            {t('taxation.updateLotPrice', {
                              value: `${newTotalPrice ?? totalPrice} ${getCurrencySymbol(
                                workingPurchaseEvent.purchaseEvent.currency
                              )}`,
                            })}
                          </Button>
                          <CustomTooltip
                            title={
                              articles.length > 0 && articles.every((a) => a.priceLock)
                                ? `${t('taxation.cannotReassignAllLocked')}`
                                : ''
                            }
                          >
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                style={{ fontSize: '0.75rem' }}
                                data-testid="re-split-btn"
                                disabled={
                                  !activeEditingMode ||
                                  !workingPurchaseEvent.purchaseEvent.price ||
                                  (articles.length > 0 && articles.every((a) => a.priceLock))
                                }
                                onClick={() => handleRunSplittingStrategy(workingPurchaseEvent)}
                              >
                                {t('taxation.reSplit')}
                              </Button>
                            </span>
                          </CustomTooltip>
                        </div>
                      </div>
                    </Grid>
                  )}
              </Grid>

              <div className="my-3 d-flex flex-row justify-content-between">
                <TableSearchNavigator
                  placeholderText={t('taxation.searchItems')}
                  searchText={searchText}
                  matchIndices={matchIndices}
                  currentMatchIndex={currentMatchIndex}
                  setSearchText={setSearchText}
                  setCurrentMatchIndex={setCurrentMatchIndex}
                  maxWidth="350px"
                />
                <div
                  className={`d-flex flex-row align-items-center ${
                    !activeEditingMode ? 'd-none' : ''
                  }`}
                  style={{ gap: 4 }}
                >
                  <Button
                    className="me-1"
                    variant="outlined"
                    disabled={articles.every((a) => a.priceLock)}
                    onClick={() => handleConvertAllItems(true)}
                  >
                    <div className="d-flex align-items-center">
                      <i className="fa-light fa-lock me-2" />
                      <span>{t('taxation.keepAllPrices')}</span>
                    </div>
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={articles.every((a) => !a.priceLock)}
                    onClick={() => handleConvertAllItems(false)}
                  >
                    <div className="d-flex align-items-center">
                      <i className="fa-light fa-lock-open me-2" />
                      <span>{t('taxation.rescaleAllPrices')}</span>
                    </div>
                  </Button>
                  <Button
                    variant="contained"
                    data-testid="save-pe"
                    color="primary"
                    disabled={
                      modifiedArticles.size === 0 &&
                      JSON.stringify({
                        ...workingPurchaseEvent.purchaseEvent,
                        seller: undefined,
                      }) ===
                        JSON.stringify({
                          ...lot,
                          seller: undefined,
                        }) &&
                      JSON.stringify(modifiedSeller) ===
                        JSON.stringify(workingPurchaseEvent.purchaseEvent?.seller ?? {})
                    }
                    onClick={() => handleSaveChanges()}
                  >
                    {t('taxation.saveAllChanges')}
                  </Button>
                </div>
              </div>
              <div className="mt-3 responsive-table-container" data-testid="purchases-table">
                <AutoSizer disableHeight>
                  {({ width }) => (
                    <Table
                      width={Math.max(width, 1100)}
                      height={500}
                      headerHeight={40}
                      rowHeight={40}
                      rowCount={articles.length}
                      rowGetter={({ index }) => articles[index]}
                      rowRenderer={rowRenderer}
                      ref={tableRef}
                    >
                      <Column
                        width={45}
                        flexGrow={0}
                        flexShrink={0}
                        label=""
                        dataKey="img"
                        headerClassName="centered-header"
                        cellRenderer={({ rowData: a }) => (
                          <ImagePopover
                            variant="iconPopover"
                            imageComponent={
                              <img width="62px" src={getProductImageUrl(a.card?._id)} />
                            }
                            imageId={a.card?._id}
                          />
                        )}
                      />

                      <Column
                        width={150}
                        flexGrow={2}
                        flexShrink={1}
                        label={t('card.name')}
                        dataKey="name"
                        cellRenderer={({ rowData: a }) => (
                          <div className="d-flex flex-column text-wrap">
                            <span>{a.card?.name}</span>
                            {a.idLanguage !== CardLanguage.English && (
                              <span>{getArticleLocalizedName(a)}</span>
                            )}
                          </div>
                        )}
                      />

                      <Column
                        width={150}
                        flexGrow={2}
                        flexShrink={1}
                        label={t('card.expansion')}
                        dataKey="expansion"
                        cellRenderer={({ rowData: a }) => (
                          <div className="d-flex flex-column">
                            <span>{a.card?.expansion}</span>
                            <span>
                              #{a.card?.cn} - {a.card?.set}
                            </span>
                          </div>
                        )}
                      />

                      <Column
                        width={150}
                        flexGrow={1}
                        flexShrink={1}
                        label={t('orders.properties')}
                        dataKey="properties"
                        cellRenderer={({ rowData: a }) => (
                          <ArticleAttributes article={a} iconHeight={20} />
                        )}
                      />

                      <Column
                        width={150}
                        flexGrow={1}
                        flexShrink={0}
                        // headerRenderer={() => (
                        //   <div className="w-100">{t('taxation.unitPrice')}</div>
                        // )}
                        label={t('taxation.unitPrice')}
                        dataKey="unitPrice"
                        cellRenderer={({ rowData: a, rowIndex }) => {
                          const originalUnit = a.price / a.quantity
                          const currentUnit =
                            (a.newPrice ?? a.price) / (a.newQuantity ?? a.quantity)
                          const hasChange = Math.abs(currentUnit - originalUnit) > 0.001
                          const increased = hasChange && currentUnit > originalUnit

                          return (
                            <div className="d-flex flex-row align-items-center justify-content-end">
                              {hasChange && (
                                <Box
                                  width={80}
                                  display="flex"
                                  justifyContent="flex-end"
                                  alignItems="center"
                                  textAlign="right"
                                >
                                  <span>
                                    {getCurrencySymbol(lot!.currency)}
                                    {originalUnit.toFixed(2)}
                                  </span>
                                  <i className="fa-regular fa-arrow-right mx-2"></i>
                                </Box>
                              )}
                              <div
                                className="d-flex flex-row align-items-center"
                                style={{ flex: 1 }}
                              >
                                <TextField
                                  value={Number(currentUnit.toFixed(2))}
                                  onChange={(e) => {
                                    const unitPrice = Number(e.target.value)
                                    const qty = a.newQuantity ?? a.quantity
                                    handleChange(
                                      Number((unitPrice * qty).toFixed(2)),
                                      rowIndex,
                                      'newPrice'
                                    )
                                  }}
                                  margin="dense"
                                  type="number"
                                  name={`unit-price-${rowIndex}`}
                                  data-testid={`edit-price-${rowIndex}`}
                                  disabled={!activeEditingMode}
                                  fullWidth
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        {getCurrencySymbol(
                                          workingPurchaseEvent.purchaseEvent.currency
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                {hasChange &&
                                  (increased ? (
                                    <i className="fa-regular fa-arrow-up success-color"></i>
                                  ) : (
                                    <i className="fa-regular fa-arrow-down error-color"></i>
                                  ))}
                              </div>
                            </div>
                          )
                        }}
                      />

                      <Column
                        width={130}
                        flexGrow={0}
                        flexShrink={0}
                        headerRenderer={() => {
                          const totalQty = articles.reduce(
                            (sum, a) => sum + (a.newQuantity ?? a.quantity),
                            0
                          )
                          const mismatch =
                            workingPurchaseEvent.purchaseEvent.quantity != null &&
                            totalQty !== workingPurchaseEvent.purchaseEvent.quantity
                          return (
                            <div className="w-100">
                              {t('card.quantity')}{' '}
                              <span
                                className={
                                  mismatch ? 'column-value value-mismatch' : 'column-value'
                                }
                              >
                                ({totalQty})
                              </span>
                            </div>
                          )
                        }}
                        label={t('card.quantity')}
                        dataKey="quantity"
                        cellRenderer={({ rowData: a, rowIndex }) => {
                          const hasNewQty =
                            a.newQuantity !== undefined && a.newQuantity !== a.quantity
                          const qtyIncreased = hasNewQty && a.newQuantity! > a.quantity

                          return (
                            <>
                              <div className="d-flex flex-row align-items-center justify-content-end px-2">
                                {hasNewQty && (
                                  <Box
                                    width={80}
                                    display={'flex'}
                                    justifyContent="flex-end"
                                    alignItems={'center'}
                                    textAlign="right"
                                  >
                                    <>
                                      <span>{a.quantity}</span>
                                      <i className="fa-regular fa-arrow-right mx-2"></i>
                                    </>
                                  </Box>
                                )}
                                <div className="d-flex flex-row align-items-center">
                                  <TextField
                                    data-testid={`edit-quantity-${rowIndex}`}
                                    value={a.newQuantity ?? a.quantity}
                                    onChange={(e) => {
                                      const raw = e.target.value

                                      // allow only strings that match with positive numbers
                                      if (raw !== '' && !/^[1-9]\d*$/.test(raw)) return
                                      // if user cancel current numer, it will be substitute with quantity 1
                                      if (raw === '') {
                                        handleChange(1, rowIndex, 'newQuantity')
                                        return
                                      }
                                      handleChange(Number(raw), rowIndex, 'newQuantity')
                                    }}
                                    margin="dense"
                                    type="number"
                                    name={`quantity-${rowIndex}`}
                                    disabled={!activeEditingMode}
                                    fullWidth
                                  />
                                  {hasNewQty &&
                                    (qtyIncreased ? (
                                      <i className="fa-regular fa-arrow-up success-color"></i>
                                    ) : (
                                      <i className="fa-regular fa-arrow-down error-color"></i>
                                    ))}
                                </div>
                              </div>
                            </>
                          )
                        }}
                      />

                      <Column
                        width={150}
                        flexGrow={0}
                        flexShrink={0}
                        headerRenderer={() => {
                          const totalPrice = articles.reduce(
                            (sum, a) => sum + (a.newPrice ?? a.price),
                            0
                          )
                          const mismatch =
                            workingPurchaseEvent.purchaseEvent.price != null &&
                            Math.abs(totalPrice - workingPurchaseEvent.purchaseEvent.price) > 0.01
                          return (
                            <div className="w-100 text-end">
                              {t('taxation.totalBuyPrice')}{' '}
                              <span
                                className={
                                  mismatch ? 'column-value value-mismatch' : 'column-value'
                                }
                              >
                                ({getCurrencySymbol(lot!.currency)} {totalPrice.toFixed(2)})
                              </span>
                            </div>
                          )
                        }}
                        label={t('taxation.totalBuyPrice')}
                        dataKey="price"
                        cellRenderer={({ rowData: a }) => (
                          <div className="text-end">
                            {getCurrencySymbol(lot!.currency)} {(a.newPrice ?? a.price).toFixed(2)}
                          </div>
                        )}
                      />

                      {activeEditingMode && (
                        <Column
                          width={100}
                          flexGrow={1}
                          flexShrink={0}
                          label={t('taxation.priceStrategy')}
                          dataKey="strategy"
                          headerRenderer={() => (
                            <div className="text-center">{t('taxation.priceStrategy')}</div>
                          )}
                          headerClassName="centered-header"
                          cellRenderer={({ rowData: a, rowIndex }) => (
                            <Box textAlign="center">
                              <AnimatedLockIcon
                                locked={a.priceLock}
                                disabled={!activeEditingMode}
                                testId={`strategy-${rowIndex}`}
                                tooltip={
                                  a.priceLock ? t('taxation.priceLocked') : t('taxation.priceAuto')
                                }
                                onClick={() => handleChange(!a.priceLock, rowIndex, 'priceLock')}
                              />
                            </Box>
                          )}
                        />
                      )}

                      {activeEditingMode && (
                        <Column
                          width={70}
                          flexGrow={0}
                          flexShrink={0}
                          label={t('common.reset')}
                          dataKey="reset"
                          headerClassName="centered-header"
                          cellRenderer={({ rowData: a, rowIndex }) => {
                            const hasChanges = modifiedArticles.has(Number(a.purchaseId))
                            const isDisabled = !activeEditingMode || !hasChanges
                            return (
                              <Box textAlign={'center'}>
                                <i
                                  data-testid={`reset-${rowIndex}`}
                                  className={`fa-light fa-arrow-rotate-left table-action-icon ${
                                    isDisabled ? 'disabled' : ''
                                  }`}
                                  onClick={() => !isDisabled && handleReset(a)}
                                ></i>
                              </Box>
                            )
                          }}
                        />
                      )}

                      {activeEditingMode && (
                        <Column
                          width={70}
                          flexGrow={0}
                          flexShrink={0}
                          label={t('common.delete')}
                          dataKey="selete"
                          headerClassName="centered-header"
                          cellRenderer={({ rowData: a, rowIndex }) => (
                            <Box textAlign={'center'}>
                              <i
                                data-testid={`delete-${rowIndex}`}
                                className={`fa-light fa-trash table-action-icon ${
                                  !activeEditingMode && 'disabled'
                                }`}
                                onClick={() => {
                                  if (!activeEditingMode) return
                                  setArticleToDelete(a)
                                }}
                              ></i>
                            </Box>
                          )}
                        />
                      )}
                    </Table>
                  )}
                </AutoSizer>
              </div>
            </div>
          )}
        </div>
      </Box>
      <ClosableDialog
        maxWidth="xl"
        open={!!articleToDelete}
        hideButton
        onClose={() => setArticleToDelete(null)}
      >
        <DialogTitle disableTypography>
          <h3>{t('common.delete')}</h3>
        </DialogTitle>
        {articleToDelete && (
          <DialogContent>
            <p>{t('taxation.confirmDeleteArticle')}</p>
            <p>
              <strong>{articleToDelete.card?.name}</strong>
              {' — '}
              {articleToDelete.card?.expansion}
            </p>
            <ArticleAttributes article={articleToDelete} iconHeight={20} />
            <p className="mt-2">
              {t('card.quantity')}: {articleToDelete.newQuantity ?? articleToDelete.quantity}
              {' · '}
              {t('taxation.totalBuyPrice')}:{' '}
              {(articleToDelete.newPrice ?? articleToDelete.price).toFixed(2)}{' '}
              {getCurrencySymbol(workingPurchaseEvent.purchaseEvent.currency)}
            </p>
          </DialogContent>
        )}
        <DialogActions>
          <div className="w-100 d-flex justify-content-evenly">
            <Button variant="outlined" onClick={() => setArticleToDelete(null)}>
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              data-testid="confirm-delete-element-btn"
              onClick={() => {
                if (articleToDelete) handleDelete(articleToDelete)
                setArticleToDelete(null)
              }}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </DialogActions>
      </ClosableDialog>
      <ClosableDialog
        maxWidth="sm"
        open={confirmDeleteReceipt}
        hideButton
        onClose={() => setConfirmDeleteReceipt(false)}
      >
        <DialogTitle disableTypography>
          <h3>{t('common.delete')}</h3>
        </DialogTitle>
        <DialogContent>
          <p>{t('taxation.confirmDeleteReceipt', { filename: uploadedReceiptName })}</p>
        </DialogContent>
        <DialogActions>
          <div className="w-100 d-flex justify-content-evenly">
            <Button variant="outlined" onClick={() => setConfirmDeleteReceipt(false)}>
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleReceiptDelete()
                setConfirmDeleteReceipt(false)
              }}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </DialogActions>
      </ClosableDialog>
    </>
  )
}
